/* eslint-disable jsx-a11y/anchor-has-content */
/* eslint-disable jsx-a11y/heading-has-content */
/* eslint-disable react/jsx-props-no-spreading */
import React, { useState } from 'react';
import { Check, Copy } from 'lucide-react';
import cn from '../../utils/styleHelpers';
import { withDraftMentions } from './messages/injectDraftMentions';

const filterProps = (props: Record<string, any>) => {
  const newProps = { ...props };
  if ('node' in newProps) delete (newProps as any).node;
  return newProps;
};

const InlineCode = ({ className, children, ...props }: any) => (
  <code className={cn(className, "relative whitespace-pre-wrap rounded-md bg-gray-100 px-1.5 py-0.5 text-sm font-medium text-gray-800 border border-gray-200")} {...props}>
    {children}
  </code>
);

const CodeBlock = ({ inline, className, children, ...props }: any) => {
  const [copied, setCopied] = useState(false);
  const match = /language-(\w+)/.exec(className || '');
  const language = match ? match[1] : '';

  if (inline) {
    return <InlineCode className={className} {...props}>{children}</InlineCode>;
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(String(children).replace(/\n$/, ''));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative group my-3 rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
      <div className="flex items-center justify-between px-3 py-1.5 text-[11px] text-gray-500 border-b border-gray-200 bg-gray-100/80">
        <span className="font-mono font-medium text-gray-600">{language || 'code'}</span>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1 hover:text-gray-800 transition-colors"
          type="button"
        >
          {copied ? <Check className="h-3 w-3 text-green-600" /> : <Copy className="h-3 w-3" />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre className="overflow-x-auto px-3 py-2.5 m-0">
        <code
          className={cn(className, "text-[13px] leading-[1.6] text-gray-800 block")}
          style={{ fontFamily: 'JetBrains Mono, Menlo, Monaco, Consolas, "Courier New", monospace' }}
        >
          {String(children).replace(/\n$/, '')}
        </code>
      </pre>
    </div>
  );
};

const AnchorLink = ({ className, ...props }: any) => (
  <a
    className={cn(className, 'cursor-pointer text-indigo-600 font-medium underline decoration-indigo-300 hover:decoration-indigo-600 hover:text-indigo-700 transition-all')}
    target="_blank"
    rel="noopener noreferrer"
    {...filterProps(props)}
  />
);

const Blockquote = ({ className, ...props }: any) => (
  <blockquote
    className={cn(className, 'border-l-4 border-indigo-500 pl-4 py-1 my-4 italic text-gray-700 bg-gray-50 rounded-r-lg')}
    {...filterProps(props)}
  />
);

const Img = ({ src, alt }: { src?: string; alt?: string }) => {
  const [error, setError] = useState(false);
  if (!src) return null;
  return (
    <div className="w-full my-4">
      {error ? (
        <div className="flex h-32 flex-col items-center justify-center gap-2 rounded-lg bg-gray-50 border border-gray-200 text-gray-500">
          <p className="text-sm font-medium text-gray-900">Image unavailable</p>
          <a href={src} target="_blank" rel="noopener noreferrer" className="max-w-xs truncate text-xs hover:underline text-gray-500">
            {src}
          </a>
        </div>
      ) : (
        // eslint-disable-next-line jsx-a11y/alt-text
        <img
          src={src}
          alt={alt ?? 'Rendered image'}
          className="w-full max-w-2xl h-auto rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 object-cover"
          onError={() => setError(true)}
        />
      )}
    </div>
  );
};

const Table = ({ className, ...props }: any) => (
  <div className="w-full my-6 overflow-hidden rounded-lg border border-gray-200 shadow-sm">
    <div className="w-full overflow-x-auto">
      <table className={cn(className, 'w-full text-left text-sm text-gray-600')} {...filterProps(props)} />
    </div>
  </div>
);
const TableHead = ({ className, ...props }: any) => (
  <thead className={cn(className, 'bg-gray-50 text-gray-900 border-b border-gray-200')} {...filterProps(props)} />
);
const TableHeadCell = ({ className, ...props }: any) => (
  <th className={cn(className, 'px-4 py-3 font-semibold')} {...filterProps(props)} />
);
const TableBody = ({ className, ...props }: any) => (
  <tbody className={cn(className, 'divide-y divide-gray-200 bg-white')} {...filterProps(props)} />
);
const TableRow = ({ className, ...props }: any) => (
  <tr className={cn(className, 'hover:bg-gray-50 transition-colors')} {...filterProps(props)} />
);
const TableCell = ({ className, ...props }: any) => (
  <td className={cn(className, 'px-4 py-3 whitespace-pre-wrap')} {...filterProps(props)} />
);

const Heading1 = ({ className, ...props }: any) => (
  <h1 className={cn(className, 'text-2xl font-bold text-gray-900 mt-8 mb-4 tracking-tight')} {...filterProps(props)} />
);
const Heading2 = ({ className, ...props }: any) => (
  <h2 className={cn(className, 'text-xl font-bold text-gray-900 mt-6 mb-3 tracking-tight border-b border-gray-100 pb-2')} {...filterProps(props)} />
);
const Heading3 = ({ className, ...props }: any) => (
  <h3 className={cn(className, 'text-lg font-semibold text-gray-900 my-2')} {...filterProps(props)} />
);

const UnorderedList = ({ className, ...props }: any) => (
  <ul className={cn(className, 'list-disc list-outside ml-5 space-y-2 text-gray-700 my-4 marker:text-gray-400')} {...filterProps(props)} />
);

const OrderedList = ({ className, ...props }: any) => (
  <ol className={cn(className, 'list-decimal list-outside ml-5 space-y-2 text-gray-700 my-4 marker:text-gray-400')} {...filterProps(props)} />
);

const ListItem = ({ className, children, ...props }: any) => (
  <li className={cn(className, 'leading-relaxed pl-1')} {...filterProps(props)}>
    {withDraftMentions(children, 'li')}
  </li>
);

const Paragraph = ({ className, children, ...props }: any) => (
  <p className={cn(className)} {...filterProps(props)}>
    {withDraftMentions(children, 'p')}
  </p>
);

export const components = {
  // Enhanced components
  code: CodeBlock,
  pre: ({ children }: any) => <div className="not-prose w-full overflow-hidden">{children}</div>,
  a: AnchorLink,
  blockquote: Blockquote,
  img: Img,

  // Lists
  ul: UnorderedList,
  ol: OrderedList,
  li: ListItem,
  p: Paragraph,

  // Table components
  table: Table,
  thead: TableHead,
  th: TableHeadCell,
  tbody: TableBody,
  tr: TableRow,
  td: TableCell,

  // Typography - Explicitly overriding these gives us more control than prose
  h1: Heading1,
  h2: Heading2,
  h3: Heading3,
  // h4-h6 will use default or we can add them if needed
};

export default components;
